import * as React from 'react';

import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { invoicesApi } from '@/api/invoices';
import { useInventoryDetail } from '@/api/stock';
import { queryKeys } from '@/lib/query-client';

import {
  buildInvoiceBackNavigationOptions,
  resolveInvoiceBackTargets,
} from '../topbarNavigation';

import { useInventoryDetailSearchController } from './useTopbarSearchControllers';

export function useInvoiceDetailTopbarState() {
  const { id: invoiceId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const invoiceBackTargets = React.useMemo(() => {
    return resolveInvoiceBackTargets(location.state);
  }, [location.state]);

  const handleInvoiceBack = React.useCallback(() => {
    const [invoiceBackTarget, ...remainingBackTargets] = invoiceBackTargets;
    if (invoiceBackTarget) {
      navigate(
        invoiceBackTarget,
        buildInvoiceBackNavigationOptions(invoiceBackTarget, remainingBackTargets),
      );
      return;
    }
    navigate('/webadmin/app/invoices', { replace: true });
  }, [invoiceBackTargets, navigate]);

  const { data: invoice } = useQuery({
    queryKey: queryKeys.invoices.detail(invoiceId!),
    queryFn: () => invoicesApi.getById(invoiceId!).then((response) => response.item),
    enabled: Boolean(invoiceId),
  });

  return {
    invoiceId,
    invoice,
    handleInvoiceBack,
  };
}

export function useInventoryDetailTopbarState() {
  const { id: inventoryId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: inventoryData } = useInventoryDetail(inventoryId ?? '');
  const searchQuery = searchParams.get('q') ?? '';
  const { searchInput, handleSearchChange } = useInventoryDetailSearchController({
    searchQuery,
    searchParams,
    setSearchParams,
  });

  const handleBack = React.useCallback(() => {
    navigate('/webadmin/app/stock/inventories');
  }, [navigate]);

  return {
    inventory: inventoryData?.inventory,
    searchInput,
    handleSearchChange,
    handleBack,
  };
}
